import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Download, Share2, Loader2 } from 'lucide-react';
import { api, getAccessToken } from '../lib/api';
import { isImage, isVideo, isAudio, isPdf, isText, formatBytes } from '../lib/utils';
import type { Node } from '@hongweibing-netdisk/shared';
import { backdrop, scaleIn } from '../lib/motion';

async function loadObjectUrl(nodeId: string): Promise<{ url: string; type: string }> {
  const url = api.nodes.downloadUrl(nodeId);
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });
  if (!res.ok) throw new Error('加载失败');
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

export function PreviewModal({ node, onClose, onDownload, onShare }: {
  node: Node;
  onClose: () => void;
  onDownload: () => void;
  onShare: () => void;
}) {
  const [object, setObject] = useState<{ url: string; type: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setObject(null);
    setError('');
    if (isImage(node.mimeType) || isPdf(node.mimeType) || isVideo(node.mimeType) || isAudio(node.mimeType) || isText(node.mimeType)) {
      loadObjectUrl(node.id)
        .then((o) => { if (alive) setObject(o); })
        .catch((e) => { if (alive) setError(e.message); });
    }
    return () => {
      alive = false;
      setObject((obj) => { if (obj) URL.revokeObjectURL(obj.url); return null; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const previewable = isImage(node.mimeType) || isVideo(node.mimeType) || isAudio(node.mimeType) || isPdf(node.mimeType) || isText(node.mimeType);

  return (
    <motion.div variants={backdrop} initial="hidden" animate="show" exit="exit" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4" onClick={onClose}>
      <motion.div variants={scaleIn} onClick={(e) => e.stopPropagation()} className="bg-bg-secondary/95 backdrop-blur-xl border border-border-soft rounded-none sm:rounded-2xl max-w-5xl w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] flex flex-col overflow-hidden soft-shadow">
        <div className="h-0.5 w-full brand-gradient" />
        {/* 头部 */}
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-b border-border-soft flex-shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-text font-medium truncate text-sm sm:text-base">{node.name}</p>
            <p className="text-xs text-text-muted">{formatBytes(node.sizeBytes)}</p>
          </div>
          <button onClick={onShare} className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-1.5 bg-card hover:bg-primary/20 text-text rounded-xl text-sm transition-colors btn-press" aria-label="分享">
            <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">分享</span>
          </button>
          <button onClick={onDownload} className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-1.5 bg-card hover:bg-primary/20 text-text rounded-xl text-sm transition-colors btn-press" aria-label="下载">
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">下载</span>
          </button>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-card text-text-muted hover:text-text transition-colors btn-press" aria-label="关闭">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-auto p-2 sm:p-4 flex items-center justify-center bg-black/30 min-h-[200px]">
          {error && <p className="text-red-400">{error}</p>}
          {!error && previewable && !object && <PreviewLoading />}
          {!error && object && isImage(node.mimeType) && (
            <img src={object.url} alt={node.name} className="max-w-full max-h-[70vh] object-contain rounded" />
          )}
          {!error && object && isVideo(node.mimeType) && (
            <video src={object.url} controls autoPlay className="max-w-full max-h-[70vh] rounded" />
          )}
          {!error && object && isAudio(node.mimeType) && (
            <audio src={object.url} controls autoPlay className="w-full max-w-lg" />
          )}
          {!error && object && (isPdf(node.mimeType) || isText(node.mimeType)) && (
            <iframe src={object.url} title={node.name} className="w-full h-[70vh] rounded bg-white" />
          )}
          {!error && !previewable && (
            <div className="flex flex-col items-center gap-3 text-text-muted">
              <Download className="w-12 h-12" />
              <p>该文件类型不支持在线预览</p>
              <button onClick={onDownload} className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg text-sm">下载查看</button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function PreviewLoading() {
  return <div className="flex items-center gap-2 text-text-muted"><Loader2 className="w-5 h-5 animate-spin" /> 加载中...</div>;
}
